/**
 * `sizes` hints for `next/image` in `LeftRight` (feature-sections/left-right.tsx).
 *
 * Mirrors `tailwind.config.mjs` `container.screens` (stepped max width) and padding
 * (2.5rem horizontal), the inner flex row’s `lg:gap-6`, and `lg:w-7/12` on the media
 * column. Inset images sit in `p-6` (3rem total horizontal), so that width is
 * subtracted.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

const CONTAINER_H_PAD = 80; // 2.5rem × 2 — matches `theme.container.padding`
const GAP = 24; // `gap-6`
const P_INSET = 48; // `p-6` horizontal (1.5rem × 2)

/**
 * Published container `max-width` for each `min-width` step (same as `container.screens`
 * in tailwind.config.mjs), applied by upper bound on the viewport.
 */
function containerBoxWidthVw(vw: number): number {
	if (vw < 1024) return vw;
	if (vw < 1280) return Math.min(vw, 1024);
	if (vw < 1420) return Math.min(vw, 1280);
	if (vw < 1600) return Math.min(vw, 1420);
	if (vw < 1800) return Math.min(vw, 1600);
	return Math.min(vw, 1800);
}

function colPxAtViewWidth(vw: number): number {
	const box = containerBoxWidthVw(vw);
	const inner = box - CONTAINER_H_PAD;
	return Math.max(1, Math.round(((inner - GAP) * 7) / 12));
}

/**
 * `sizes` use `max-width` media; pick the **largest** viewport in each band so the
 * selected file covers the real slot at that resolution.
 */
function buildColSizes(): string {
	const parts = [
		"(max-width: 1023px) 100vw",
		`(max-width: 1279px) ${colPxAtViewWidth(1279)}px`,
		`(max-width: 1419px) ${colPxAtViewWidth(1419)}px`,
		`(max-width: 1599px) ${colPxAtViewWidth(1599)}px`,
		`(max-width: 1799px) ${colPxAtViewWidth(1799)}px`,
		`${colPxAtViewWidth(2000)}px`,
	];
	return parts.join(", ");
}

function buildInsetSizes(): string {
	const parts = [
		"(max-width: 1023px) 100vw",
		`(max-width: 1279px) ${Math.max(1, colPxAtViewWidth(1279) - P_INSET)}px`,
		`(max-width: 1419px) ${Math.max(1, colPxAtViewWidth(1419) - P_INSET)}px`,
		`(max-width: 1599px) ${Math.max(1, colPxAtViewWidth(1599) - P_INSET)}px`,
		`(max-width: 1799px) ${Math.max(1, colPxAtViewWidth(1799) - P_INSET)}px`,
		`${Math.max(1, colPxAtViewWidth(2000) - P_INSET)}px`,
	];
	return parts.join(", ");
}

export const leftRightColImageSizes = buildColSizes();
export const leftRightInsetImageSizes = buildInsetSizes();
